import type { NavigationAudience } from './navigation/types';
import { theme } from '../tailwind.config';

const WEBSITE_NAME = 'Fantasy Premier League Tips by Fantasy Football Hub';
// The brand on its own, for og:site_name and title suffixes. WEBSITE_NAME above
// is the longer keyword-bearing form some page titles use.
const SITE_NAME = 'Fantasy Football Hub';
const COOKIE_NAME = 'hubUser';
const CMS_HOSTNAME = 'https://cms.fantasyfootballhub.co.uk';
const FFH_HOSTNAME = 'https://www.fantasyfootballhub.co.uk';

// Ben Crellin's calendar is a Sanity article (FFH-2630), not the hardcoded
// route it used to live on. Linking straight at /articles keeps in-app
// navigation off the legacy path: a client-side click there fetches
// _next/data, which a redirect answers with a bare 308 the Next router does
// not follow, leaving the page to render without props. The path is spelled
// out rather than imported from lib/sanity, which carries the read token.
export const CRELLIN_CALENDAR_PATH = '/articles/crellin-calendar';

const DEFAULT_BLOG_IMAGE = '/images/default-blog-image.png';

export const APP_STORE_URL =
  'https://apps.apple.com/app/fantasy-football-hub-fpl-tips/id1513399239';
export const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=uk.co.fantasyfootballhub.app';

// PayPal holds the renewal date, the amount and the cancellation control for
// PayPal subscriptions, so members are sent to the page that lists them rather
// than to the PayPal homepage.
export const PAYPAL_AUTOPAY_URL = 'https://www.paypal.com/myaccount/autopay/';

// Both store badge assets share a ~3.37:1 aspect ratio.
export const STORE_BADGE_HEIGHT = 48;
export const STORE_BADGE_WIDTH = 162;

export const NEXT_LOADING_STATES = {
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated'
};

export const POSITION_IDS = {
  GOALKEEPER_POSITION_ID: 1,
  DEFENDER_POSITION_ID: 2,
  MIDFIELDER_POSITION_ID: 3,
  FORWARD_POSITION_ID: 4
};

export const CURRENT_YEAR = new Date().getFullYear();

export const MY_TEAM_PAGES = [
  { slug: 'points', name: 'Live' },
  { slug: 'pick', name: 'Pick' },
  { slug: 'transfers', name: 'AI Transfers' },
  { slug: 'ai-teams', name: 'AI Teams' },
  { slug: 'mini-leagues', name: 'Mini Leagues' }
];

export const MY_TEAM_SLUGS = MY_TEAM_PAGES.map((page) => page.slug);

const CURRENT_SEASON = 2026;

// Earliest season with stats data in the public API
const EARLIEST_STATS_SEASON = 2018;

const FIXTURE_OPTIONS = Array.from(
  { length: CURRENT_SEASON - EARLIEST_STATS_SEASON + 1 },
  (_, index) => {
    const seasonStartYear = EARLIEST_STATS_SEASON + index;
    const seasonEndLabel = (seasonStartYear + 1).toString(10).slice(-2);
    return {
      value: seasonStartYear,
      label: `${seasonStartYear}/${seasonEndLabel}`
    };
  }
);

// Number of GWs for my-team predictions horizon
const PREDICTION_HORIZON = 5;

const REDUCE_HORIZON_BEFORE_GAMEWEEKS: number[] = [38];
const UNLIMITED_TRANSFER_GAMEWEEKS: number[] = [];

// Marketing bar
const SLOGAN = 'Win at FPL';
const MARKETING_TEXTS = [SLOGAN];

const COLORS = {
  primary1: '#202323',
  primary2: '#00ACED',
  primary2highlight: '#53d0ffcc',
  gray1: '#AEB4B8',
  gray2: theme.colors.neutral[300],
  gray3: '#EFEFEF',
  green: '#00EEBB',
  midGreen: '#22c55e',
  red: '#FF0051',
  purple: '#A81379',
  yellow: '#FFFD5D',
  azure: '#0078A3',
  darkRed: '#BC453F',
  orange: '#FBB03B',
  black: '#000000',
  white: '#FFFFFF',
  hubBlueLight: '#3F4545',
  hubBlueLighter: '#5B6464',
  hubBlueDark: '#0077A4',
  hubBlueDarker: '#005D80'
};

export type NavBarType = {
  name: string;
  url?: string;
  root?: string;
  isTopLevel?: boolean;
  hasGameweek?: boolean;
  isHighlightedOnHomepage?: boolean;
  level: string;
  isHiddenForGuests?: boolean;
  dataCy?: string;
  shortName?: string;
  hasSubMenu?: boolean;
  hideSubNav?: boolean;
  type?: string;
  isGridView?: boolean;
  noChildLinks?: boolean;
  isHiddenOnXs?: boolean;
  isOpenInNewWindow?: boolean;
  /** Who sees the item. Defaults to everyone. */
  audience?: NavigationAudience;
};

const NAV_DATA: NavBarType[] = [
  /*  MY TEAM */
  {
    name: 'My Team',
    url: '/my-team',
    shortName: 'My Team',
    isTopLevel: true,
    level: '1',
    hasSubMenu: true
  },
  {
    name: 'MY TEAM',
    type: 'divider',
    level: '1.1'
  },
  {
    name: 'Pick & Rating',
    shortName: 'Pick & Rating',
    url: '/my-team/pick',
    level: '1.2',
    hideSubNav: true
  },
  {
    name: 'AI Transfers',
    shortName: 'AI Transfers',
    url: '/my-team/transfers',
    level: '1.3',
    hideSubNav: true
  },
  {
    name: 'AI Teams',
    shortName: 'AI Teams',
    url: '/my-team/ai-teams',
    level: '1.4',
    hideSubNav: true
  },
  {
    name: 'Live Rank',
    shortName: 'Live Rank',
    url: '/my-team/points',
    level: '1.5',
    hideSubNav: true
  },
  {
    name: 'Mini-Leagues & Awards',
    shortName: 'Mini-Leagues',
    url: '/my-team/mini-leagues',
    level: '1.6',
    hideSubNav: true
  },
  /* TOOL BOX */
  {
    name: 'Toolbox',
    url: '/tools',
    shortName: 'Tools',
    isTopLevel: true,
    level: '2',
    hasSubMenu: true,
    isGridView: true
  },
  {
    name: 'TOOLS',
    type: 'divider',
    level: '2.1'
  },
  {
    name: 'OPTA Stats',
    shortName: 'OPTA Stats',
    url: '/opta',
    level: '2.2'
  },
  {
    name: 'Player Points Predictions',
    url: '/predictions',
    level: '2.3'
  },
  {
    name: 'Fixture Analyser',
    url: '/fixture-ticker',
    level: '2.4'
  },
  {
    name: 'Player Comparison',
    url: '/fpl-player-comparison-tool',
    level: '2.5'
  },
  {
    name: 'Price Changes',
    url: '/fantasy-premier-league-price-rises',
    level: '2.6'
  },
  {
    name: 'Player Profiles',
    url: '/player-profile',
    level: '2.7'
  },
  {
    name: 'Match Centre',
    url: '/fpl-match-centre',
    level: '2.8'
  },
  {
    name: 'FPL Live Rank',
    url: '/my-team/points',
    level: '2.9'
  },
  {
    name: 'CALENDARS & PLANNERS',
    type: 'divider',
    level: '2.12'
  },
  {
    name: "Ben Crellin's Planner",
    url: '/ben-crellins-fpl-transfer-planning-sheet',
    level: '2.13'
  },
  {
    name: "Ben Crellin's FPL Calendar",
    url: CRELLIN_CALENDAR_PATH,
    level: '2.14'
  },
  {
    name: "Jossy's Planner",
    url: '/jossys-fpl-planner',
    level: '2.15'
  },

  /* TEAM REVEALS */
  {
    name: 'Expert Reveals',
    shortName: 'Reveals',
    url: '/category/team-reveal',
    isTopLevel: true,
    isHiddenOnXs: false,
    level: '3',
    hasSubMenu: true,
    isGridView: true
  },
  {
    name: 'EXPERT REVEALS',
    type: 'divider',
    level: '3.1'
  },
  {
    name: 'Team Reveal Bios',
    url: '/articles/fantasy-premier-league-team-reveals',
    level: '3.2'
  },
  {
    name: 'FPL Salah',
    url: '/team-reveals/fplsalah',
    level: '3.3'
  },
  {
    name: 'BigManBakar',
    url: '/team-reveals/bigmanbakar',
    level: '3.4'
  },
  {
    name: 'FPL Matthew',
    url: '/team-reveals/fplmatthew',
    level: '3.5'
  },
  {
    name: 'Rich Clarke',
    url: '/team-reveals/richclarke',
    level: '3.6'
  },
  {
    name: 'FPL Heisenberg',
    url: '/team-reveals/fplheisenberg',
    level: '3.7'
  },
  {
    name: 'Fergi',
    url: '/team-reveals/fergi',
    level: '3.8'
  },
  {
    name: 'Holly Shand',
    url: '/team-reveals/hollyshand',
    level: '3.9'
  },
  {
    name: 'Hub AI Team',
    url: '/team-reveals/hubai/team-reveal',
    level: '3.10'
  },
  {
    name: 'Ben Crellin',
    url: '/team-reveals/bencrellin',
    level: '3.11'
  },
  {
    name: 'Lee Giles',
    url: '/team-reveals/leegiles/team-reveal',
    level: '3.12'
  },
  {
    name: 'FPL Fran',
    url: '/team-reveals/fplfran/team-reveal',
    level: '3.13'
  },
  {
    name: 'FPL Tips',
    url: '/team-reveals/fpltips/team-reveal',
    level: '3.14'
  },
  {
    name: 'Jack Ukiah',
    url: '/team-reveals/jackukiah/team-reveal',
    level: '3.15'
  },
  {
    name: 'Gianni Buttice',
    url: '/team-reveals/giannibuttice/team-reveal',
    level: '3.16'
  },
  {
    name: 'FPL WIRE',
    type: 'divider',
    level: '3.17'
  },
  {
    name: 'Zophar',
    url: '/team-reveals/zophar/team-reveal',
    level: '3.18'
  },
  {
    name: 'Lateriser',
    url: '/team-reveals/lateriser/team-reveal',
    level: '3.19'
  },
  {
    name: 'Pras',
    url: '/team-reveals/pras/team-reveal',
    level: '3.20'
  },
  /* CONTENT */
  {
    name: 'Content',
    url: '/articles/fantasy-premier-league-ultimate-guide-fpl-tips',
    isTopLevel: true,
    level: '4',
    hasSubMenu: true,
    isGridView: true
  },
  {
    name: 'ARTICLES',
    type: 'divider',
    level: '4.1'
  },
  {
    name: 'GW$ Tips',
    url: '/articles/fantasy-premier-league-ultimate-guide-fpl-tips',
    hasGameweek: true,
    level: '4.2'
  },
  {
    name: 'Latest FPL Articles',
    url: '/category/fpl',
    level: '4.4'
  },
  {
    name: 'How to Play FPL',
    url: '/articles/how-to-play-fpl',
    level: '4.5'
  },
  {
    name: 'Chip Strategy Guide',
    url: '/articles/fpl-chip-strategy-guide',
    level: '4.6'
  },
  {
    name: 'Predicted Lineups & Team News',
    url: '/predicted-lineups',
    level: '4.7'
  },
  {
    name: 'Blank & Double Gameweek Guide',
    url: '/articles/fpl-blank-double-gameweek-guide',
    level: '4.8'
  },
  {
    name: 'Best Captain',
    url: '/category/fpl-captain',
    level: '4.9'
  },
  {
    name: 'Set Piece Takers',
    url: '/articles/premier-league-set-piece-takers',
    level: '4.10'
  },

  /* FORMATS */
  {
    name: 'FORMATS',
    type: 'divider',
    level: '4.11'
  },
  {
    name: 'FPL',
    url: '/category/fplfree',
    level: '4.12'
  },
  {
    name: 'Telegraph',
    url: '/category/telegraph',
    level: '4.13'
  },
  {
    name: 'Sun Dream Team',
    url: '/category/sun-dream-team',
    level: '4.14'
  },
  /* MEDIA */
  {
    name: 'MEDIA',
    type: 'divider',
    level: '4.15'
  },
  {
    name: 'Videos',
    url: '/youtube-videos',
    level: '4.16'
  },
  {
    name: 'The Green Arrow',
    url: '/the-green-arrow-fpl-podcast',
    root: '/',
    level: '4.17'
  },
  {
    name: 'TFF Podcast',
    url: '/telegraph-fantasy-football-podcast',
    level: '4.18'
  },

  /* MORE */
  {
    name: 'More',
    url: '/whatsapp',
    root: '/',
    isTopLevel: true,
    level: '5',
    hasSubMenu: true
  },
  {
    name: 'SERVICES',
    type: 'divider',
    level: '5.1'
  },
  {
    name: 'WhatsApp Group',
    url: '/whatsapp',
    level: '5.2'
  },
  {
    name: 'Ultra',
    url: '/how-it-works/ultra',
    audience: 'nonUltra',
    level: '5.3'
  },
  {
    name: "Ultra Members' Area",
    url: '/ultra-service-members-area',
    audience: 'ultra',
    level: '5.4'
  },
  {
    name: 'SUPPORT',
    type: 'divider',
    level: '5.5'
  },
  {
    name: 'Help Centre',
    url: 'https://fantasyfootballhub.freshdesk.com/support/home',
    level: '5.6',
    isOpenInNewWindow: true
  },
  {
    name: 'OTHER',
    type: 'divider',
    level: '5.7'
  },
  {
    name: 'Discord',
    url: 'https://discord.gg/mk3ythgGsr',
    level: '5.8',
    isOpenInNewWindow: true
  },
  {
    name: 'Try Mobile App',
    url: 'https://app.fantasyfootballhub.co.uk/',
    level: '5.9',
    isOpenInNewWindow: true
  },
  {
    name: 'Contributors',
    url: '/contributors',
    level: '5.10'
  },
  {
    name: 'Become an Affiliate',
    url: '/become-an-affiliate',
    level: '5.11'
  },
  {
    name: 'Welcome',
    url: '/welcome',
    level: '5.12'
  },
  {
    name: 'App',
    url: 'https://onelink.to/ffhubapp',
    isTopLevel: true,
    noChildLinks: true,
    isOpenInNewWindow: true,
    level: '7'
  },
  {
    name: 'AI Team Rating',
    url: '/ai-team-rating',
    isTopLevel: true,
    noChildLinks: true,
    audience: 'loggedOut',
    level: '8'
  },
  {
    name: 'Account',
    url: '/account',
    isHiddenForGuests: true,
    level: '9.1'
  },
  {
    name: 'Upgrade',
    url: '/member-upgrade',
    isHiddenForGuests: true,
    level: '9.2'
  },
  {
    name: 'Notifications',
    url: '/notifications',
    isHiddenForGuests: true,
    level: '9.3'
  }
];

const CATEGORY_MAP = {
  podcasts: '/category/podcasts',
  'fantasy premier league': '/category/fpl',
  'sun dream team': '/category/sun-dream-team',
  telegraph: '/category/telegraph',
  fanteam: '/category/fanteam'
};

const PLAN_NAMES = {
  ULTRA: 'Ultra',
  PRO: 'Pro',
  STARTER: 'Starter',
  FREE: 'Free plan'
} as const;

const MEMBERSHIPS = {
  ULTRA_MONTHLY_ID: 28119,
  ULTRA_YEARLY_ID: 11652,
  STANDARD_YEARLY_ID: 5871,
  STANDARD_MONTHLY_ID: 591,
  STARTER_MONTHLY_ID: 55336,
  STARTER_YEARLY_ID: 55341,
  PREMIUM_MONTHLY_ID: 5889,
  PREMIUM_YEARLY_ID: 7343,
  PRO_MONTHLY_ID: 55343,
  PRO_YEARLY_ID: 55345,
  FREE_ID: 714,
  AUTHOR_ID: 4995,
  FANTEAM_PREMIUM_ID: 34665,
  FANTEAM_STANDARD_ID: 34662,
  FANTEAM_ULTRA_ID: 34671,
  // FFH-1744 alt-pricing arm. Same Starter/Pro yearly tiers at a higher price,
  // sold only from the /ai-team-rating-v2 funnel. Synthetic IDs, well clear of
  // the WordPress-era range the others come from.
  STARTER_YEARLY_ALT_ID: 100000,
  PRO_YEARLY_ALT_ID: 100001
};

// Year-one purchase value reported to the ad platforms on the browser dataLayer
// rail (resolvePurchaseValue). An unmapped plan id reports 0.
//
// FFH-1744: the Starter row was keyed on STARTER_MONTHLY_ID, but 29.88 is the
// Starter *yearly* year-one price and Starter Monthly has no Stripe price
// configured in the API, so it cannot be purchased through this path at all.
// The effect was that every Starter yearly conversion reported £0 while the
// unsellable monthly plan held the value. Re-keyed onto STARTER_YEARLY_ID.
const ANNUAL_VALUES_FOR_MEMBERSHIPS = {
  [MEMBERSHIPS.STARTER_YEARLY_ID]: 29.88,
  [MEMBERSHIPS.PRO_YEARLY_ID]: 47.88,
  [MEMBERSHIPS.ULTRA_YEARLY_ID]: 179.88,
  // Alt-pricing arm: 50% off the higher prices in year one.
  [MEMBERSHIPS.STARTER_YEARLY_ALT_ID]: 35.88,
  [MEMBERSHIPS.PRO_YEARLY_ALT_ID]: 59.88
};

const TIER_TO_MEMBERSHIP = {
  free: 714,
  premium_yearly: 7343,
  standard_yearly: 5871,
  premium_monthly: 5889,
  standard_monthly: 591,
  starter_monthly: 55336,
  starter_yearly: 55341,
  pro_monthly: 55343,
  pro_yearly: 55345,
  ultra_monthly: 28119,
  ultra_yearly: 11652,
  // FFH-1744 alt-pricing arm. The keys are Stripe price lookup keys and must
  // match the tierCard.plan enum in Sanity exactly.
  starter_yearly_alt: MEMBERSHIPS.STARTER_YEARLY_ALT_ID,
  pro_yearly_alt: MEMBERSHIPS.PRO_YEARLY_ALT_ID
};

const PAID_MEMBERSHIP_IDS = [
  MEMBERSHIPS.ULTRA_YEARLY_ID,
  MEMBERSHIPS.ULTRA_MONTHLY_ID,
  MEMBERSHIPS.STARTER_MONTHLY_ID,
  MEMBERSHIPS.STARTER_YEARLY_ID,
  MEMBERSHIPS.STANDARD_YEARLY_ID,
  MEMBERSHIPS.STANDARD_MONTHLY_ID,
  MEMBERSHIPS.PRO_MONTHLY_ID,
  MEMBERSHIPS.PRO_YEARLY_ID,
  MEMBERSHIPS.PREMIUM_YEARLY_ID,
  MEMBERSHIPS.PREMIUM_MONTHLY_ID,
  MEMBERSHIPS.AUTHOR_ID,
  MEMBERSHIPS.FANTEAM_PREMIUM_ID,
  MEMBERSHIPS.FANTEAM_STANDARD_ID,
  MEMBERSHIPS.FANTEAM_ULTRA_ID
];

const PAID_MEMBERSHIP_LEVELS = {
  [MEMBERSHIPS.STARTER_MONTHLY_ID]: { LEVEL: 1 },
  [MEMBERSHIPS.STARTER_YEARLY_ID]: { LEVEL: 1 },
  [MEMBERSHIPS.STANDARD_MONTHLY_ID]: { LEVEL: 2 },
  [MEMBERSHIPS.STANDARD_YEARLY_ID]: { LEVEL: 2 },
  [MEMBERSHIPS.FANTEAM_STANDARD_ID]: { LEVEL: 2 },
  [MEMBERSHIPS.PRO_MONTHLY_ID]: { LEVEL: 3 },
  [MEMBERSHIPS.PRO_YEARLY_ID]: { LEVEL: 3 },
  [MEMBERSHIPS.PREMIUM_MONTHLY_ID]: { LEVEL: 4 },
  [MEMBERSHIPS.PREMIUM_YEARLY_ID]: { LEVEL: 4 },
  [MEMBERSHIPS.FANTEAM_PREMIUM_ID]: { LEVEL: 4 },
  [MEMBERSHIPS.ULTRA_MONTHLY_ID]: { LEVEL: 5 },
  [MEMBERSHIPS.ULTRA_YEARLY_ID]: { LEVEL: 5 },
  [MEMBERSHIPS.FANTEAM_ULTRA_ID]: { LEVEL: 5 },
  [MEMBERSHIPS.AUTHOR_ID]: { LEVEL: 5 }
};

// Freshdesk hosted ticket forms that replace the legacy hub API form submissions.
const FRESHDESK_FORMS = {
  AFFILIATE_SIGN_UP:
    'https://fantasyfootballhub.freshdesk.com/support/tickets/new?ticket_form=affiliate_sign_up',
  WHATSAPP_ADMIN:
    'https://fantasyfootballhub.freshdesk.com/support/tickets/new?ticket_form=whatsapp_admin'
};

const EXCLUDED_PATHS = [
  'fpl-rmt-tool',
  'ben-crellins-fpl-transfer-planning-sheet',
  'fplapp',
  'contributor-portal',
  'fpl-player-comparison-tool',
  'fantasy-premier-league-live-rank',
  'fantasy-premier-league-price-rises',
  'opta',
  'fixture-ticker',
  'player-profile',
  'predictions',
  'my-team',
  'join',
  'about',
  'ultra-service-members-area',
  'contributors',
  '/en/contributors'
];

// Evergreen pages served by the WordPress article template. Their publish date
// never changes but the content does, so the datestamp only ever reads as stale
// (FFH-2026). News articles keep theirs.
const EVERGREEN_ARTICLE_SLUGS = ['whatsapp'];

const TOOLBOX_ITEMS = [
  {
    title: 'My Team',
    description:
      'Optimise, monitor and manage your FPL team with customised tips',
    link: '/my-team',
    icon: 'calendar',
    dataCy: 'my-team-link'
  },
  {
    title: 'OPTA Stats',
    description:
      'The go-to place for all the latest fantasy football statistics',
    link: '/opta',
    icon: 'bar-chart',
    dataCy: 'opta-stats-link'
  },
  {
    title: 'Player Points Predictions',
    description: 'Our predicted player points for every gameweek',
    link: '/predictions',
    icon: 'points-prediction',
    dataCy: 'points-predictions-link'
  },
  {
    title: 'Fixture Ticker',
    description: 'A good fixture ticker should be every manager’s best friend',
    link: '/fixture-ticker',
    icon: 'event-note',
    dataCy: 'fixture-ticker-link'
  },
  {
    title: 'Player Comparisons',
    description: 'Debating between players for your fantasy team?',
    link: '/fpl-player-comparison-tool',
    icon: 'group',
    dataCy: 'player-comparisons-link'
  },
  {
    title: 'Player Profiles',
    description: 'Search for statistics and heatmaps on a single player',
    link: '/player-profile',
    icon: 'person-running',
    dataCy: 'player-profiles-link'
  },
  {
    title: 'Price Changes',
    description: 'Price change predictor to plan your next transfers',
    link: '/fantasy-premier-league-price-rises',
    icon: 'arrow-sort',
    dataCy: 'price-rises'
  },
  {
    title: 'Ben Crellin’s FPL Calendar',
    description: 'Ben Crellin’s FPL Calendar',
    link: CRELLIN_CALENDAR_PATH,
    icon: 'event-note',
    dataCy: 'crellin-fpl-calendar-link'
  },
  {
    title: 'Ben Crellin’s Planner',
    description: 'The only transfer planner spreadsheet you need',
    link: '/ben-crellins-fpl-transfer-planning-sheet',
    icon: 'list',
    dataCy: 'ben-crellins-planner-link'
  },
  {
    title: 'Jossy’s Planner',
    description:
      'Jossy’s clever FPL planning sheet has a number of key tools to help fantasy managers',
    link: '/jossys-fpl-planner',
    icon: 'list',
    dataCy: 'jossys-planner-link'
  },
  {
    title: 'Match Centre',
    description: 'View FPL scores update live',
    link: '/fpl-match-centre',
    icon: 'sports-soccer',
    dataCy: 'match-centre-link'
  },
  {
    title: 'FPL Live Rank',
    description: 'View FPL scores update live',
    link: '/my-team/points',
    icon: 'show-chart',
    dataCy: 'fpl-live-rank-link'
  },
  {
    title: 'WhatsApp Groups',
    description: 'Join our WhatsApp groups to discuss more',
    link: '/whatsapp',
    icon: 'whats-app',
    dataCy: 'whatsapp-groups-link'
  }
];

const PLAN_LIST_ITEMS = [
  'New “My Team” feature',
  'Transfer suggestion algorithm',
  'Powerful points projections',
  'Expert team reveals',
  'Customisable OPTA Stats',
  'Player comparison tool',
  "Ben Crellin's transfer planner",
  'Advanced fixture analyser',
  '300+ member only articles a year',
  'See your live FPL rank',
  'Price change predictor',
  'Full access to all tools',
  'Premium WhatsApp groups',
  'Team review service'
];

const MIN_FPL_PLAYER_COST = 3.5;
const MAX_FPL_PLAYER_COST = 16;

const GKP_POSITION_ID = 1;
const DEF_POSITION_ID = 2;
const MID_POSITION_ID = 3;
const FWD_POSITION_ID = 4;

const FIXTURE_TICKER_PATH = '/fixture-ticker';
const LIVE_RANK_PATH = '/fantasy-premier-league-live-rank';

const FOOTER_TOOLS_LINKS = [
  {
    title: 'My Team',
    path: '/my-team'
  },
  {
    title: 'OPTA Stats',
    path: '/opta'
  },
  {
    title: 'Points Prediction',
    path: '/predictions'
  },
  {
    title: 'Player Comparison',
    path: '/fpl-player-comparison-tool'
  }
];

const FOOTER_GUIDES_LINKS = [
  {
    title: 'Fantasy Premier League',
    path: '/category/fpl/'
  },
  {
    title: 'Sun Dream Team',
    path: '/category/sun-dream-team'
  }
];

const MORE_FOOTER_LINKS = [
  {
    title: 'WhatsApp Groups ',
    path: '/whatsapp'
  },
  {
    title: 'Account',
    path: '/account'
  },
  {
    title: 'Become An Affiliate',
    path: '/become-an-affiliate'
  },
  {
    title: 'Cookie Preferences',
    id: '#cky-preferences-link',
    path: '/not-used'
  }
];

const HUB_FOOTER_LINKS = [
  {
    title: "What's new",
    path: '/whats-new'
  },
  {
    title: 'Contact Us',
    path: '/contact',
    isOpenInNewWindow: true
  },
  {
    title: 'Partner with us',
    path: 'https://partnerships.fantasyfootballhub.co.uk',
    isOpenInNewWindow: true
  },
  {
    title: 'Terms & Conditions',
    path: '/terms-and-conditions'
  },
  {
    title: 'Privacy Policy',
    path: '/privacy-policy'
  },
  {
    title: 'Cookie Policy',
    path: '/cookie-policy'
  },
  {
    title: 'About Us',
    path: '/about'
  }
];

const SOCIAL_LINKS = [
  {
    href: 'https://www.facebook.com/ffootballhub/',
    title: 'Facebook',
    pathDrawn:
      'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z'
  },

  {
    href: 'https://www.instagram.com/ffh_hq',
    title: 'Instagram',
    pathDrawn:
      'M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z'
  },

  {
    href: 'https://twitter.com/FFH_HQ',
    title: 'Twitter',
    pathDrawn:
      'M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84'
  },

  {
    href: 'https://www.youtube.com/c/FantasyFootballHub/?sub_confirmation=1',
    title: 'YouTube',
    pathDrawn:
      'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'
  }
];

const BREAKPOINTS = {
  mobile: 360,
  xs: 420,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280
};

const STORAGE_ITEMS = {
  KEY_FPL_ID: 'fpl_id'
};

const LOGIN_OUTCOMES = {
  SUCCESS: 'LOGIN_SUCCESS',
  FAILED: 'LOGIN_FAILED'
};
const MAX_GAMEWEEK = 38;
const GAMEWEEK_ONE = 1;

const MIN_GW = 1;

const MEMBERS_ONLY_WORDPRESS_CATEGORY = 30;

const AFFILIATE_WORDPRESS_CATEGORY = 315;

const TEAM_REVEAL_WORDPRESS_CATEGORY = 305;

const APP_PAYMENT_GATEWAYS = [
  'qyg3tm-7aw',
  'qyg3ui-4d9',
  'renz2w-4s6',
  'renz4a-4tu'
];

const LOADING_ANIMATION_ACTIONS = {
  RECOMMENDED_TRANSFERS: [
    'Analysing team',
    'Analysing Chip Strategy',
    'Generating Recommended Transfer(s)'
  ],
  LIVE_POINTS: ['Fetching live points'],
  MY_TEAM: ['Importing team', 'Analysing team', 'Optimising team'],
  CHECKOUT_PAYMENT_PROVIDERS: ['Fetching Payment Options'],
  MATCH_CENTRE: ['Fetching fixtures'],
  FREE_HIT_TEAM: ['Fetching best Free Hit Team'],
  WILDCARD_TEAM: ['Fetching best Wildcard Team'],
  ONBOARDING_TEAM_SELECTION: ['Fetching your team picker'],
  MINI_LEAGUE_ANALYSER: ['Fetching your mini league data']
};

const FPL_CHIPS = {
  FREE_HIT: 'freehit',
  WILDCARD: 'wildcard',
  BENCH_BOOST: 'bboost',
  TRIPLE_CAPTAIN: '3xc',
  MANAGER: 'manager'
};

const STANDARD_MEMBER_HAS_SEEN_MEMBESHIP_INFO_MODAL =
  'standardMemberHasSeenMembershipInfoModal';

const AI_SIGN_UP_FLOW_TEAM_PLAYER_IDS = 'aiSignUpFlowPlayerIds';
const UNAUTHENTICATED_FPL_ID = 'unauthenticatedFplId';

// adds 5% buffer onto estimated prices
const EXCHANGE_RATE_BUFFER = 1.05;

const TOP_FOREIGN_CURRENCIES = [
  'USD',
  'EUR',
  'NOK',
  'CAD',
  'AUD',
  'SEK',
  'INR',
  'SGD',
  'MYR',
  'ZAR',
  'SAR',
  'AED',
  'PLN',
  'DKK',
  'NZD',
  'DZD',
  'KWD',
  'RUB',
  'IDR',
  'THB'
];

const INITIAL_FPL_BANK = 1000;

const OPTIMISATION_WEIGHTING = 0.8;

const TEN_MINUTES_IN_MS = 600000;

const FPL_SEASON_PLAYER_NUMBERS = {
  '2024/25': 11500000,
  '2023/24': 10910020,
  '2022/23': 11400000,
  '2021/22': 9100000,
  '2020/21': 8200000,
  '2019/20': 7600000,
  '2018/19': 6300000,
  '2017/18': 5200000,
  '2016/17': 4500000,
  '2015/16': 3700000,
  '2014/15': 3500000,
  '2013/14': 3200000,
  '2012/13': 2610000,
  '2011/12': 2780000,
  '2010/11': 2400000,
  '2009/10': 2100000,
  '2008/09': 1900000,
  '2007/08': 1700000,
  '2006/07': 1300000,
  '2005/06': 700000,
  '2004/05': 472251,
  '2003/04': 312026,
  '2002/03': 76284
};

const RECAPTCHA_TYPES = { VERSION_TWO: 'v2', VERSION_THREE: 'v3' };

const TEAM_SHORT_NAME_TO_CODE_MAP = {
  ARS: 3,
  AVL: 7,
  BHA: 36,
  BOU: 91,
  BRE: 94,
  CHE: 8,
  CRY: 31,
  EVE: 11,
  FUL: 54,
  IPS: 40,
  LEI: 13,
  LIV: 14,
  MCI: 43,
  MUN: 1,
  NEW: 4,
  NFO: 17,
  SOU: 20,
  TOT: 6,
  WHU: 21,
  WOL: 39
};

export const TEAM_CODE_TO_SHORT_NAME_MAP = Object.fromEntries(
  Object.entries(TEAM_SHORT_NAME_TO_CODE_MAP).map(([name, code]) => [
    code,
    name
  ])
);

const NEW_USER_THRESHOLD_2026 = 450000;

// Upper bound used to validate user-supplied FPL team IDs. The official FPL
// player count for the 2025/26 season sits around 11M, so 15M gives plenty
// of headroom while still catching typo'd numbers. FFH-1200 tracks replacing
// this with a live value from /league/context.
const MAX_FPL_TEAM_ID = 15_000_000;

const FFH_NAMESPACE = 'https://fantasyfootballhub.co.uk';
const FFH_ROLES = `${FFH_NAMESPACE}/roles`;
const FFH_SUBSCRIPTION = `${FFH_NAMESPACE}/subscription`;
const FFH_EXTERNAL_ID = `${FFH_NAMESPACE}/externalUserId`;
const FFH_PROFILE_ID = `${FFH_NAMESPACE}/profileId`;
const CROWDSIGNAL_POLL_SCRIPT_URL = 'https://secure.polldaddy.com/w/64912.js';

export {
  WEBSITE_NAME,
  COOKIE_NAME,
  CMS_HOSTNAME,
  NAV_DATA,
  CATEGORY_MAP,
  PAID_MEMBERSHIP_IDS,
  MEMBERSHIPS,
  PLAN_NAMES,
  EXCLUDED_PATHS,
  EVERGREEN_ARTICLE_SLUGS,
  FRESHDESK_FORMS,
  TOOLBOX_ITEMS,
  DEFAULT_BLOG_IMAGE,
  COLORS,
  MIN_FPL_PLAYER_COST,
  MAX_FPL_PLAYER_COST,
  FFH_HOSTNAME,
  SITE_NAME,
  GKP_POSITION_ID,
  DEF_POSITION_ID,
  MID_POSITION_ID,
  FWD_POSITION_ID,
  PLAN_LIST_ITEMS,
  FIXTURE_TICKER_PATH,
  LIVE_RANK_PATH,
  MARKETING_TEXTS,
  FOOTER_TOOLS_LINKS,
  FOOTER_GUIDES_LINKS,
  HUB_FOOTER_LINKS,
  MORE_FOOTER_LINKS,
  SOCIAL_LINKS,
  BREAKPOINTS,
  STORAGE_ITEMS,
  SLOGAN,
  LOGIN_OUTCOMES,
  MAX_GAMEWEEK,
  GAMEWEEK_ONE,
  MIN_GW,
  MEMBERS_ONLY_WORDPRESS_CATEGORY,
  CURRENT_SEASON,
  FIXTURE_OPTIONS,
  AFFILIATE_WORDPRESS_CATEGORY,
  TEAM_REVEAL_WORDPRESS_CATEGORY,
  PREDICTION_HORIZON,
  APP_PAYMENT_GATEWAYS,
  REDUCE_HORIZON_BEFORE_GAMEWEEKS,
  UNLIMITED_TRANSFER_GAMEWEEKS,
  LOADING_ANIMATION_ACTIONS,
  FPL_CHIPS,
  PAID_MEMBERSHIP_LEVELS,
  STANDARD_MEMBER_HAS_SEEN_MEMBESHIP_INFO_MODAL,
  EXCHANGE_RATE_BUFFER,
  TOP_FOREIGN_CURRENCIES,
  INITIAL_FPL_BANK,
  OPTIMISATION_WEIGHTING,
  AI_SIGN_UP_FLOW_TEAM_PLAYER_IDS,
  FPL_SEASON_PLAYER_NUMBERS,
  UNAUTHENTICATED_FPL_ID,
  RECAPTCHA_TYPES,
  TEN_MINUTES_IN_MS,
  TIER_TO_MEMBERSHIP,
  NEW_USER_THRESHOLD_2026,
  MAX_FPL_TEAM_ID,
  FFH_ROLES,
  FFH_EXTERNAL_ID,
  FFH_PROFILE_ID,
  FFH_SUBSCRIPTION,
  ANNUAL_VALUES_FOR_MEMBERSHIPS,
  CROWDSIGNAL_POLL_SCRIPT_URL
};

export const BLANK = 'blank';

export const LIVE_RANK_MAINTENANCE_MESSAGE =
  'The official FPL site is updating. Live rank will be available again shortly.';

export const MY_TEAM_MAINTENANCE_MESSAGE =
  'The official FPL site is updating. My Team will be available again shortly.';
